import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { RegistryMigrationService } from './registry-migration.service';
import { ServerService } from './server.service';
import { Wallet } from './wallet';

const WELCOMES_SEEN_KEY = 'onboarding_welcomes_seen_v1';

@Injectable({ providedIn: 'root' })
export class OnboardingModalService {
  private readonly wallet = inject(Wallet);
  private readonly server = inject(ServerService);
  private readonly registryMigration = inject(RegistryMigrationService);
  private readonly router = inject(Router);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );
  // /wiki is meant to be a shareable reference page — don't interrupt it with signup.
  private readonly onWiki = computed(() => this.currentUrl().startsWith('/wiki'));

  private readonly _showModal = signal<boolean>(false);
  private readonly _editMode = signal<boolean>(false);
  private readonly _welcomesSeen = signal<boolean>(
    localStorage.getItem(WELCOMES_SEEN_KEY) === 'true',
  );

  readonly showModal = this._showModal.asReadonly();
  readonly editMode = this._editMode.asReadonly();
  readonly hasSeenWelcomes = this._welcomesSeen.asReadonly();

  markWelcomesSeen(): void {
    localStorage.setItem(WELCOMES_SEEN_KEY, 'true');
    this._welcomesSeen.set(true);
  }

  private autoOpened = false;

  constructor() {
    // After either firing, further setup is gated on the user explicitly
    // clicking the Set Up Team CTA so we don't interrupt browsing.
    effect(() => {
      if (this.autoOpened) return;
      if (this.onWiki()) return;
      // Hold off while a returning player is being migrated (or that decision is
      // still pending) — they get imported silently, not pushed through setup.
      if (this.registryMigration.decisionPending()) return;
      if (!this._welcomesSeen()) {
        this.autoOpened = true;
        this._showModal.set(true);
        return;
      }
      if (!localStorage.getItem(this.wallet.USERNAME_KEY)) {
        this.autoOpened = true;
        this._showModal.set(true);
      }
    });
  }

  open() {
    this._editMode.set(false);
    this._showModal.set(true);
  }

  openForEdit() {
    const addr = this.wallet.walletAddress();
    if (this.server.twitterHandleFor(addr)) {
      return;
    }
    this._editMode.set(true);
    this._showModal.set(true);
  }

  close() {
    this._showModal.set(false);
    this._editMode.set(false);
  }
}
